/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import { v4 as uuidv4 } from "uuid";
import { Production } from "../Constants";
interface MSALAuthConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  knownAuthorities: string[];
}

interface MSALCacheConfig {
  storeAuthStateInCookie: boolean;
}
interface MsalConfig {
  auth: MSALAuthConfig;
  cache: MSALCacheConfig;
}

interface APIConfig {
  retryCount: number;
  baseUrl: string;
  getBlobAccessToken: string;
  blobEndPoint: string;
  statusUpdate: string;
  getReferenceMetaData: string;
  referenceHistory: string;
  referenceHistoryBlob: string;
}
interface telemetryConfig {
  enableHeartMetrics: boolean;
  instrumentationKey: string;
  service: string;
  serviceLine: string;
  serviceOffering: string;
  componentName: string;
  subComponentName: string;
  componentId: string;
  application: string;
  sessionId: string;
  environmentName: string;
  defaultCandidateId: string;
}

interface OcvConfig{
  appId: string,
  area: string
}

interface ApplicationConfig {
  careersPortalLink: string;
  msalConfig: MsalConfig;
  monitoringEndPointURL: string;
  loginRequestScope: {
    scopes: string[];
  };
  api: APIConfig;
  telemetry: telemetryConfig;
  ocv: OcvConfig;
  production: boolean;
}

export const config: ApplicationConfig = {
  careersPortalLink: `${process.env.REACT_APP_CAREERS_PORTAL_URI}`,
  monitoringEndPointURL: process.env.REACT_APP_MONITORING_SERVICE || "",
  msalConfig: {
    auth: {
      clientId: `${process.env.REACT_APP_MSAL_CLIENT_ID}`,
      authority: `${process.env.REACT_APP_MSAL_AUTHORITY}`,
      redirectUri: `${window.location.origin}`,
      knownAuthorities: [`${process.env.REACT_APP_MSAL_KNOWN_AUTHORITY_1}`],
    },
    cache: {
      storeAuthStateInCookie: false,
    },
  },
  loginRequestScope: {
    scopes: [`${process.env.REACT_APP_MSAL_LOGIN_REQUEST_SCOPE_1}`],
  },
  api: {
    retryCount: 3,
    baseUrl: `${process.env.REACT_APP_API_BASE_URL}`,
    getBlobAccessToken: "/v1/Reference/blob",
    statusUpdate: "/v1/Reference/status",
    blobEndPoint: `https://${process.env.REACT_APP_BLOB_ACCOUNT_STORAGE}.blob.core.windows.net`,
    getReferenceMetaData: "/v1/Reference",
    referenceHistory:
      "/v1/Reference/{candidateId}/referencehistory?referenceProviderEmail={referenceProviderEmail}",
    referenceHistoryBlob: "v1/Reference/{candidateId}/document/{referenceId}",
  },
  ocv: {
    appId: process.env.REACT_APP_OCV_APPID || "",
    area: process.env.REACT_APP_OCV_AREA || "Global Careers Site",
  },
  production:
    process.env.REACT_APP_ENVIRONMENT_NAME &&
    process.env.REACT_APP_ENVIRONMENT_NAME === Production
      ? true
      : false,
  telemetry: {
    enableHeartMetrics:
      Boolean(typeof process.env.REACT_APP_ENABLEHEARTMETRICS) || false,
    instrumentationKey:
      process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY || "",

    serviceOffering: process.env.REACT_APP_TELEMETRY_SERVICEOFFERING || "",
    serviceLine: process.env.REACT_APP_TELEMETRY_SERVICELINE || "",
    service: process.env.REACT_APP_TELEMETRY_SERVICE || "",
    componentName: process.env.REACT_APP_TELEMETRY_COMPONENTNAME || "",
    subComponentName: process.env.REACT_APP_TELEMETRY_SUBCOMPONENTNAME || "",
    componentId: process.env.REACT_APP_TELEMETRY_COMPONENTID || "",
    application: process.env.REACT_APP_TELEMETRY_COMPONENTNAME || "",
    sessionId: uuidv4(),
    environmentName: process.env.REACT_APP_ENVIRONMENT_NAME || "",
    defaultCandidateId: "-1",
  }
};
